import type { Ref } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { useLegacyAreaDialogNavigation } from "@/components/address-selector/composables/useLegacyAreaDialogNavigation";
import { getCityByPrefGroupedByStepA } from "@/common/graphql/cityByPref";
import { getSectionByPrefCityGroupedByStepA } from "@/common/graphql/sectionByPrefCity";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  GetBlockByPrefCitySectionDocument,
  GetBlockByPrefCitySectionQuery,
  GetBlockByZipCodeDocument,
  GetBlockByZipCodeQuery,
  GetDeliveryAreaDocument,
  GetDeliveryAreaQuery,
  GetPrefByChainDocument,
  GetPrefByChainQuery,
  GetZipCodeByPrefCitySectionBlockDocument,
  GetZipCodeByPrefCitySectionBlockQuery,
  GetZipCodeByPrefCitySectionDocument,
  GetZipCodeByPrefCitySectionQuery,
  ListDeliveryAreaDocument,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ListDeliveryAreaSectionUnassignedDocument,
  ListDeliveryAreaSectionUnassignedQuery,
} from "@/generated/graphql";
import type { DeliveryArea, DeliveryAvailabilityData, KanaStepA, ZipAddressName } from "../types";

/**
 * 住所選択/エリア選択まわりの状態と操作に必要な依存をまとめた型。
 */
interface UseDeliveryRequestRegisterAddressParams {
  wordList: Ref<Record<string, string>>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  pref_name: Ref<string>;
  pref_name_befor: Ref<string>;
  zip_code: Ref<string>;
  btnNonMemberToggle: Ref<number>;
  errorZipCode: Ref<string>;
  errorPrefSelect: Ref<string>;
  userErrorMessage: Ref<string>;
  prefListItems: Ref<Array<string | null> | null | undefined>;
  prefSelectValue: Ref<string>;
  addressDialog: Ref<boolean>;
  addressFixed: Ref<boolean>;
  areaDialog: Ref<boolean>;
  cityBlockList: Ref<KanaStepA | null | undefined>;
  sectionBlockList: Ref<KanaStepA | null | undefined>;
  blockList: Ref<Array<{ name: string | null | undefined; enabled: boolean } | null | undefined>>;
  unassignedBlockList: Ref<Array<string | null | undefined>>;
  addressBlockList: Ref<Array<string | null> | null | undefined>;
  zipAddressList: Ref<Array<ZipAddressName | null | undefined> | null | undefined>;
  zipCodeFromPref: Ref<string>;
  originalAddress: Ref<string>;
  originalSectionAddress: Ref<string>;
  originalCityAddress: Ref<string>;
  originalBlockName: Ref<string>;
  clickMode: Ref<boolean>;
  listDeliveryArea: Ref<DeliveryArea[] | null>;
  deliveryAreaName: Ref<string>;
  acceptableEndTime: Ref<string>;
  deliveryAvailabilityHeader: Ref<Map<string, string>>;
  deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  disp_deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  deliveryId_0_Flg: Ref<boolean>;
  deliveryStartHourItem: Ref<number[]>;
  deliveryStartHourValues: Ref<number | undefined>;
  deliveryStartMinitValues: Ref<{ label: string; deliveryStartMinit: number } | undefined>;
  displayDeliveryTime: Ref<string>;
  deliverySelectDayBox: Ref<string>;
  deliverySelectBox: Ref<string>;
  deliveryStartTime: Ref<string>;
  deliveryRequestCreateInput: any;
  getDeliveryTimeList: () => Promise<void>;
}

/**
 * 配達依頼登録の住所確定、配達エリア取得、住所ダイアログ制御を扱う composable。
 */
export const useDeliveryRequestRegisterAddress = ({
  wordList,
  errdialog,
  errMessage,
  pref_name,
  pref_name_befor,
  zip_code,
  btnNonMemberToggle,
  errorZipCode,
  errorPrefSelect,
  userErrorMessage,
  prefListItems,
  prefSelectValue,
  addressDialog,
  addressFixed,
  areaDialog,
  cityBlockList,
  sectionBlockList,
  blockList,
  unassignedBlockList,
  addressBlockList,
  zipAddressList,
  zipCodeFromPref,
  originalAddress,
  originalSectionAddress,
  originalCityAddress,
  originalBlockName,
  clickMode,
  listDeliveryArea,
  deliveryAreaName,
  acceptableEndTime,
  deliveryAvailabilityHeader,
  deliveryAvailabilityData,
  disp_deliveryAvailabilityData,
  deliveryId_0_Flg,
  deliveryStartHourItem,
  deliveryStartHourValues,
  deliveryStartMinitValues,
  displayDeliveryTime,
  deliverySelectDayBox,
  deliverySelectBox,
  deliveryStartTime,
  deliveryRequestCreateInput,
  getDeliveryTimeList,
}: UseDeliveryRequestRegisterAddressParams) => {
  const initializeAddress = async (): Promise<void> => {
    addressFixed.value = false;
    const apolloClient = await generateApolloClient();

    originalAddress.value = pref_name.value;

    if (pref_name.value !== "") {
      cityBlockList.value = await getCityByPrefGroupedByStepA(
        localStorageGetCustomerCode(),
        localStorageGetChainCode(),
        localStorageGetShopCode(),
        pref_name.value
      );
      btnNonMemberToggle.value = 1;
    }

    if (zip_code.value !== "") {
      btnNonMemberToggle.value = 0;
      originalAddress.value =
        pref_name.value + originalCityAddress.value + originalSectionAddress.value;

      try {
        const adddressBlock: ApolloQueryResult<GetBlockByZipCodeQuery> =
          await apolloClient.query<GetBlockByZipCodeQuery>({
            query: GetBlockByZipCodeDocument,
            variables: {
              customer_code: localStorageGetCustomerCode(),
              chain_code: localStorageGetChainCode(),
              shop_code: localStorageGetShopCode(),
              zip_code: zip_code.value,
            },
            fetchPolicy: "no-cache",
          });

        cityBlockList.value = null;
        zipAddressList.value = adddressBlock.data.getBlockByZipCode;
        zipCodeFromPref.value = zip_code.value;

        if (!zipAddressList.value || zipAddressList.value.length == 0) {
          // 配達可能な住所がない場合は住所選択ダイアログにエラーを表示する
          errorZipCode.value = "入力した郵便番号はご利用対象外です";
          return;
        } else if (zipAddressList.value.length == 1) {
          if (zipAddressList.value[0]) {
            pref_name.value = zipAddressList.value[0]["pref_name"];
            originalCityAddress.value = zipAddressList.value[0][
              "city_name"
            ].replace("　", "");
            originalSectionAddress.value = zipAddressList.value[0][
              "section_name"
            ].replace("　", "");

            originalBlockName.value = "";
            if (zipAddressList.value[0]["block_name"]) {
              originalBlockName.value = zipAddressList.value[0][
                "block_name"
              ].replace("　", "");
            }
            await getAreaList();
          }
          areaDialog.value = false;
          blockList.value = [];
          clickMode.value = false;
          deliveryRequestCreateInput.delivery_zip_code = zip_code.value;
          addressFixed.value = true;
        } else {
          let prev_city_name = "";
          let prev_section_name = "";
          let city_fixed = true;
          let section_fixed = true;

          for (const zipAddress of zipAddressList.value) {
            if (zipAddress && zipAddress["section_name"]) {
              if (prev_city_name == "") {
                prev_city_name = zipAddress["city_name"];
              } else if (prev_city_name != zipAddress["city_name"]) {
                city_fixed = false;
              }

              if (prev_section_name == "") {
                pref_name.value = zipAddress["pref_name"];
                prev_section_name = zipAddress["section_name"];
              } else if (prev_section_name != zipAddress["section_name"]) {
                section_fixed = false;
              }
            }
          }

          if (city_fixed && section_fixed) {
            blockList.value = [];
            unassignedBlockList.value = [];
            for (const zipAddress of zipAddressList.value) {
              if (zipAddress) {
                blockList.value.push({
                  name:
                    zipAddress["block_name"] &&
                    zipAddress["block_name"].trim() !== ""
                      ? zipAddress["block_name"]
                      : wordList.value.lbl_other_block_disp_name,
                  enabled: true,
                });
              }
            }

            if (zipAddressList.value[0]) {
              prefSelectValue.value = zipAddressList.value[0]["pref_name"];
              originalCityAddress.value = zipAddressList.value[0][
                "city_name"
              ].replace("　", "");
              originalSectionAddress.value = zipAddressList.value[0][
                "section_name"
              ].replace("　", "");

              await setDeliveryAreaSectionUnassigned(zip_code.value);
            }
          } else if (city_fixed) {
            sectionBlockList.value = null;

            if (zipAddressList.value[0]) {
              originalCityAddress.value = zipAddressList.value[0][
                "city_name"
              ].replace("　", "");
            }

            sectionBlockList.value = {
              line_a: [],
              line_ka: [],
              line_sa: [],
              line_ta: [],
              line_na: [],
              line_ha: [],
              line_ma: [],
              line_ya: [],
              line_ra: [],
              line_wa: [],
              line_other: [],
            };
            let prev_name = "";
            for (const zipAddress of zipAddressList.value) {
              if (zipAddress) {
                const kana_step_a = zipAddress["section_kana"].substring(0, 1);

                if (prev_name != zipAddress["section_name"]) {
                  prev_name = zipAddress["section_name"];
                  if (
                    kana_step_a == "ｱ" ||
                    kana_step_a == "ｲ" ||
                    kana_step_a == "ｳ" ||
                    kana_step_a == "ｴ" ||
                    kana_step_a == "ｵ"
                  ) {
                    sectionBlockList.value.line_a?.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  } else if (
                    kana_step_a == "ｶ" ||
                    kana_step_a == "ｷ" ||
                    kana_step_a == "ｸ" ||
                    kana_step_a == "ｹ" ||
                    kana_step_a == "ｺ" ||
                    kana_step_a == "ｶﾞ" ||
                    kana_step_a == "ｷﾞ" ||
                    kana_step_a == "ｸﾞ" ||
                    kana_step_a == "ｹﾞ" ||
                    kana_step_a == "ｺﾞ"
                  ) {
                    sectionBlockList.value.line_ka?.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  } else if (
                    kana_step_a == "ｻ" ||
                    kana_step_a == "ｼ" ||
                    kana_step_a == "ｽ" ||
                    kana_step_a == "ｾ" ||
                    kana_step_a == "ｿ" ||
                    kana_step_a == "ｻﾞ" ||
                    kana_step_a == "ｼﾞ" ||
                    kana_step_a == "ｽﾞ" ||
                    kana_step_a == "ｾﾞ" ||
                    kana_step_a == "ｿﾞ"
                  ) {
                    sectionBlockList.value.line_sa?.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  } else if (
                    kana_step_a == "ﾀ" ||
                    kana_step_a == "ﾁ" ||
                    kana_step_a == "ﾂ" ||
                    kana_step_a == "ﾃ" ||
                    kana_step_a == "ﾄ" ||
                    kana_step_a == "ﾀﾞ" ||
                    kana_step_a == "ﾁﾞ" ||
                    kana_step_a == "ﾂﾞ" ||
                    kana_step_a == "ﾃﾞ" ||
                    kana_step_a == "ﾄﾞ"
                  ) {
                    sectionBlockList.value.line_ta?.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  } else if (
                    kana_step_a == "ﾅ" ||
                    kana_step_a == "ﾆ" ||
                    kana_step_a == "ﾇ" ||
                    kana_step_a == "ﾈ" ||
                    kana_step_a == "ﾉ"
                  ) {
                    sectionBlockList.value.line_na?.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  } else if (
                    kana_step_a == "ﾊ" ||
                    kana_step_a == "ﾋ" ||
                    kana_step_a == "ﾌ" ||
                    kana_step_a == "ﾍ" ||
                    kana_step_a == "ﾎ"
                  ) {
                    sectionBlockList.value.line_ha?.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  } else if (
                    kana_step_a == "ﾏ" ||
                    kana_step_a == "ﾐ" ||
                    kana_step_a == "ﾑ" ||
                    kana_step_a == "ﾒ" ||
                    kana_step_a == "ﾓ"
                  ) {
                    sectionBlockList.value.line_ma?.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  } else if (
                    kana_step_a == "ﾔ" ||
                    kana_step_a == "ﾕ" ||
                    kana_step_a == "ﾖ"
                  ) {
                    sectionBlockList.value.line_ya?.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  } else if (
                    kana_step_a == "ﾗ" ||
                    kana_step_a == "ﾘ" ||
                    kana_step_a == "ﾙ" ||
                    kana_step_a == "ﾚ" ||
                    kana_step_a == "ﾛ"
                  ) {
                    sectionBlockList.value.line_ra?.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  } else if (kana_step_a == "ﾜ") {
                    sectionBlockList.value.line_wa?.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  } else {
                    sectionBlockList.value.line_other?.push(
                      zipAddress["section_name"].replace("　", "")
                    );
                  }
                }
              }
            }
          }
          areaDialog.value = true;
        }
      } catch (err: unknown) {
        if (err instanceof Error) {
          err.message = "丁目の取得に失敗しました。";
          throw err;
        } else {
          throw new Error("丁目の取得に失敗しました。");
        }
      } finally {
        clickMode.value = false;
      }
    }
  };

  const onClickAddressUpdate = async (): Promise<void> => {
    pref_name_befor.value = pref_name.value;
    await areaAddresSelectClear();

    const apolloClient = await generateApolloClient();

    const prefList: ApolloQueryResult<GetPrefByChainQuery> =
      await apolloClient.query<GetPrefByChainQuery>({
        query: GetPrefByChainDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
        },
        fetchPolicy: "no-cache",
      });
    prefListItems.value = prefList.data.getPrefByChain;
    addressDialog.value = true;
  };

  const onClickAddressChangeBtn = async (): Promise<void> => {
    if (clickMode.value) {
      return;
    }

    clickMode.value = true;
    try {
      addressFixed.value = false;
      if (btnNonMemberToggle.value == 0) {
        // 検索押下時に対象外判定を再実行し、エラーがあれば後続処理を止める。
        await onZipCodeInput();
        if (errorZipCode.value) {
          return;
        }
      }

      if (btnNonMemberToggle.value == 1) {
        pref_name.value = prefSelectValue.value;
        zip_code.value = "";
      } else {
        zipCodeFromPref.value = zip_code.value;
        prefSelectValue.value = "";
      }

      await initializeAddress();

      // 郵便番号検索でエラーが発生した場合は住所選択ダイアログに留まる
      if (errorZipCode.value) {
        return;
      }

      if (!addressFixed.value) {
        areaDialog.value = true;
      }
      addressDialog.value = false;
    } finally {
      clickMode.value = false;
    }
  };

  const onClickCityBlockLink = async (
    item: string | null | undefined
  ): Promise<void> => {
    if (clickMode.value == true) {
      return;
    } else {
      clickMode.value = true;
    }

    try {
      sectionBlockList.value = await getSectionByPrefCityGroupedByStepA(
        localStorageGetCustomerCode(),
        localStorageGetChainCode(),
        localStorageGetShopCode(),
        pref_name.value,
        item
      );
      originalCityAddress.value = item ?? "";
      blockList.value = [];
      // 市区町村再選択時は前回丁目判定を引き継がない。
      unassignedBlockList.value = [];
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = "町村の取得に失敗しました。";
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "町村の取得に失敗しました。（Unknown Error）";
      }

      errdialog.value = true;
      await areaClear();
      return;
    } finally {
      clickMode.value = false;
    }
  };

  const onClickSectionBlockLink = async (
    item: string | null | undefined
  ): Promise<void> => {
    if (!item) {
      return;
    }
    if (clickMode.value == true) {
      return;
    } else {
      clickMode.value = true;
    }

    try {
      const apolloClient = await generateApolloClient();

      if (item == wordList.value.lbl_other_block_disp_name) {
        item = "";
      }

      try {
        const getZipCode: ApolloQueryResult<GetZipCodeByPrefCitySectionQuery> =
          await apolloClient.query<GetZipCodeByPrefCitySectionQuery>({
            query: GetZipCodeByPrefCitySectionDocument,
            variables: {
              customer_code: localStorageGetCustomerCode(),
              chain_code: localStorageGetChainCode(),
              shop_code: localStorageGetShopCode(),
              city: originalCityAddress.value,
              pref: pref_name.value,
              section: item,
            },
            fetchPolicy: "no-cache",
          });
        zipCodeFromPref.value = getZipCode.data.getZipCodeByPrefCitySection!;
        deliveryRequestCreateInput.delivery_zip_code = zipCodeFromPref.value;
      } catch (err: unknown) {
        if (err instanceof Error) {
          err.message = "該当する郵便番号が見つかりませんでした。";
          throw err;
        } else {
          throw new Error("該当する郵便番号が見つかりませんでした。");
        }
      }

      try {
        const Block: ApolloQueryResult<GetBlockByPrefCitySectionQuery> =
          await apolloClient.query<GetBlockByPrefCitySectionQuery>({
            query: GetBlockByPrefCitySectionDocument,
            variables: {
              customer_code: localStorageGetCustomerCode(),
              chain_code: localStorageGetChainCode(),
              shop_code: localStorageGetShopCode(),
              city: originalCityAddress.value,
              pref: pref_name.value,
              section: item,
            },
            fetchPolicy: "no-cache",
          });
        blockList.value = Block.data.getBlockByPrefCitySection!.map(
          (block: string | null | undefined) => ({
            name:
              block && block.trim() !== ""
                ? block
                : wordList.value.lbl_other_block_disp_name,
            enabled: true,
          })
        );
        originalSectionAddress.value = item ?? "";
      } catch (err: unknown) {
        if (err instanceof Error) {
          err.message = "丁目の取得に失敗しました。";
          throw err;
        } else {
          throw new Error("丁目の取得に失敗しました。");
        }
      }

      if (
        blockList.value.length <= 0 ||
        (blockList.value.length == 1 &&
          blockList.value[0]?.name === wordList.value.lbl_other_block_disp_name)
      ) {
        blockList.value = [];
        unassignedBlockList.value = [];
        deliveryRequestCreateInput.delivery_address1 =
          pref_name.value + originalCityAddress.value + originalSectionAddress.value;
        deliveryRequestCreateInput.delivery_zip_code = zipCodeFromPref.value;
        await getAreaList();

        areaDialog.value = false;
        clickMode.value = false;
      }

      await setDeliveryAreaSectionUnassigned(zipCodeFromPref.value);
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = err.message;
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "不明なエラーが発生しました。";
      }
      errdialog.value = true;
      await areaClear();
      return;
    } finally {
      clickMode.value = false;
    }
  };

  const setDeliveryAreaSectionUnassigned = async (
    zipCode: string
  ): Promise<void> => {
    if (
      !blockList.value!.some(
        (block) => block?.name !== wordList.value.lbl_other_block_disp_name
      )
    ) {
      return;
    }

    try {
      const apolloClient = await generateApolloClient();
      const selectedAddress =
        pref_name.value + originalCityAddress.value + originalSectionAddress.value;

      const result: ApolloQueryResult<ListDeliveryAreaSectionUnassignedQuery> =
        await apolloClient.query<ListDeliveryAreaSectionUnassignedQuery>({
          query: ListDeliveryAreaSectionUnassignedDocument,
          variables: {
            input: {
              customer_code: localStorageGetCustomerCode(),
              chain_code: localStorageGetChainCode(),
              shop_code: localStorageGetShopCode(),
              zip_code: zipCode,
            },
          },
          fetchPolicy: "no-cache",
        });
      if (
        result.data.listDeliveryAreaSectionUnassigned !== undefined &&
        result.data.listDeliveryAreaSectionUnassigned !== null
      ) {
        blockList.value = (blockList.value || []).concat(
          result.data.listDeliveryAreaSectionUnassigned!.map((block) => ({
            name:
              block?.delivery_area_section?.replace(selectedAddress, "") ||
              wordList.value.lbl_other_block_disp_name,
            enabled: false,
          }))
        );

        unassignedBlockList.value = blockList.value
          .filter((block) => block && !block.enabled)
          .map((block) => block!.name || wordList.value.lbl_other_block_disp_name);
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = "丁目の取得に失敗しました。";
        throw err;
      } else {
        throw new Error("丁目の取得に失敗しました。");
      }
    }
  };

  const onClickAddressBlockLink = async (
    item: string | null | undefined
  ): Promise<void> => {
    if (clickMode.value == true) {
      return;
    } else {
      clickMode.value = true;
    }

    try {
      const apolloClient = await generateApolloClient();

      let blockName = item;
      if (blockName === wordList.value.lbl_other_block_disp_name) {
        blockName = "";
      }

      const getZipCode: ApolloQueryResult<GetZipCodeByPrefCitySectionBlockQuery> =
        await apolloClient.query<GetZipCodeByPrefCitySectionBlockQuery>({
          query: GetZipCodeByPrefCitySectionBlockDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
            city: originalCityAddress.value,
            pref: pref_name.value,
            section: originalSectionAddress.value,
            block: blockName,
          },
          fetchPolicy: "no-cache",
        });
      zipCodeFromPref.value = getZipCode.data.getZipCodeByPrefCitySectionBlock!;
      deliveryRequestCreateInput.delivery_zip_code = zipCodeFromPref.value;
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = "該当する郵便番号が見つかりませんでした。";
        throw err;
      } else {
        throw new Error("該当する郵便番号が見つかりませんでした。");
      }
    }

    try {
      deliveryRequestCreateInput.delivery_zip_code = zipCodeFromPref.value;
      await getAreaList(item);
    } catch (err: unknown) {
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
      errdialog.value = true;

      await areaClear();
      return;
    } finally {
      areaDialog.value = false;
      blockList.value = [];
      unassignedBlockList.value = [];
      clickMode.value = false;
    }
  };

  const onClickListDeliveryArea = async (item: DeliveryArea): Promise<void> => {
    deliveryAvailabilityHeader.value = new Map();
    disp_deliveryAvailabilityData.value = [];
    let addAry: DeliveryAvailabilityData[] = [];

    deliveryRequestCreateInput.area_code = item.area_code;
    deliveryAreaName.value = item.area_name;
    acceptableEndTime.value = item.acceptable_end_time;

    displayDeliveryTime.value = "";
    deliverySelectDayBox.value = "-999";
    deliverySelectBox.value = "-999";

    deliveryAvailabilityData.value.forEach((listVal) => {
      addAry = [];
      listVal.forEach((value) => {
        if (String(value.area_code) == String(item.area_code)) {
          addAry.push(value);
          if (value.delivery_id_desc !== "") {
            deliveryAvailabilityHeader.value.set(
              value.delivery_id_desc,
              value.delivery_id_desc
            );
          }

          if (listVal[0].delivery_id == 0) {
            deliveryId_0_Flg.value = true;
            deliveryStartTime.value = listVal[0].delivery_start_time;
          } else {
            deliveryId_0_Flg.value = false;
            deliveryStartTime.value = "";
          }
        }
      });

      if (addAry.length > 0) {
        disp_deliveryAvailabilityData.value.push(addAry);
      }
    });
  };

  const getAreaList = async (item?: string | null): Promise<void> => {
    deliveryAvailabilityHeader.value = new Map();
    deliveryAvailabilityData.value = [];
    disp_deliveryAvailabilityData.value = [];
    deliveryId_0_Flg.value = false;
    deliveryStartHourItem.value = [];
    deliveryStartHourValues.value = undefined;
    deliveryStartMinitValues.value = undefined;
    displayDeliveryTime.value = "";
    listDeliveryArea.value = null;
    deliveryRequestCreateInput.area_code = "";

    if (item == wordList.value.lbl_other_block_disp_name) {
      item = "";
    }
    originalBlockName.value = item ? item : originalBlockName.value;

    if (originalAddress.value === "" || originalAddress.value == pref_name.value) {
      originalAddress.value =
        pref_name.value + originalCityAddress.value + originalSectionAddress.value;
    }

    try {
      const apolloClient = await generateApolloClient();

      const listArea: ApolloQueryResult<ListDeliveryAreaQuery> =
        await apolloClient.query<
          ListDeliveryAreaQuery,
          ListDeliveryAreaQueryVariables
        >({
          query: ListDeliveryAreaDocument,
          variables: {
            chain_code: localStorageGetChainCode(),
            customer_code: localStorageGetCustomerCode(),
            shop_code: localStorageGetShopCode(),
            address: originalAddress.value + originalBlockName.value,
          },
        });

      const areaList: DeliveryArea[] = (listArea.data.listDeliveryArea ?? [])
        .filter((area): area is NonNullable<typeof area> => area !== null)
        .map((area) => ({
          __typename: area.__typename,
          area_code: area.area_code,
          area_name: area.area_name,
          area_name_kana: area.area_name_kana,
          payment_method: area.payment_method,
          acceptable_end_time: area.acceptable_end_time,
        }));
      listDeliveryArea.value = areaList;
      if (areaList.length === 0) {
        throw new Error("配達エリア一覧の取得に失敗しました。");
      }
      deliveryRequestCreateInput.delivery_address1 =
        originalAddress.value + originalBlockName.value;

      await getAreaInfo();

      if (areaList.length == 1) {
        deliveryRequestCreateInput.area_code = areaList[0].area_code;
        await onClickListDeliveryArea(areaList[0]);
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = "配達エリア一覧の取得に失敗しました。";
        throw err;
      } else {
        throw new Error("配達エリア一覧の取得に失敗しました。");
      }
    }
  };

  const getAreaInfo = async (): Promise<void> => {
    const apolloClient = await generateApolloClient();

    let area_data: ApolloQueryResult<GetDeliveryAreaQuery> | null = null;
    let acceptableEndTimeAry: string[] = [];
    const formatAcceptableEndTime = (value?: string | null): string | null => {
      if (!value) {
        return null;
      }
      const parts = value.split(":");
      if (parts.length < 2) {
        return value;
      }
      return parts[0].padStart(2, "0") + ":" + parts[1].padStart(2, "0");
    };

    try {
      for (let i = 0; i < listDeliveryArea.value!.length; i++) {
        const area = listDeliveryArea.value![i];
        if (!area) {
          continue;
        }

        let deliveryArea = area;

        try {
          area_data = await apolloClient.query<GetDeliveryAreaQuery>({
            query: GetDeliveryAreaDocument,
            variables: {
              chain_code: localStorageGetChainCode(),
              customer_code: localStorageGetCustomerCode(),
              shop_code: localStorageGetShopCode(),
              area_code: area.area_code,
            },
            fetchPolicy: "no-cache",
          });
          if (area_data.data.getDeliveryArea) {
            deliveryArea = {
              ...deliveryArea,
              ...area_data.data.getDeliveryArea,
            };
          }
        } catch {
          // 一覧情報で継続する
        }

        const formattedAcceptableEndTime = formatAcceptableEndTime(
          deliveryArea.acceptable_end_time
        );
        if (formattedAcceptableEndTime) {
          acceptableEndTimeAry = formattedAcceptableEndTime.split(":");
        }
        listDeliveryArea.value![i] = {
          ...area,
          ...deliveryArea,
          acceptable_end_time:
            formattedAcceptableEndTime ?? area.acceptable_end_time,
        };

        if (area.area_code == deliveryRequestCreateInput.area_code) {
          deliveryAreaName.value = deliveryArea.area_name ?? area.area_name;
          acceptableEndTime.value =
            formattedAcceptableEndTime ?? area.acceptable_end_time ?? "";
        }
        area_data = null;
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = "配達エリアの取得に失敗しました。";
        throw err;
      } else {
        throw new Error("配達エリアの取得に失敗しました。");
      }
    }

    await getDeliveryTimeList();
  };

  const onZipCodeInput = async (): Promise<void> => {
    const apolloClient = await generateApolloClient();

    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";

    if (zip_code.value.length != 7) {
      return;
    }
    const adddressBlock: ApolloQueryResult<GetBlockByZipCodeQuery> =
      await apolloClient.query<GetBlockByZipCodeQuery>({
        query: GetBlockByZipCodeDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
          zip_code: zip_code.value,
        },
        fetchPolicy: "no-cache",
      });

    if (
      !adddressBlock.data.getBlockByZipCode ||
      adddressBlock.data.getBlockByZipCode.length == 0
    ) {
      errorZipCode.value = "入力した郵便番号はご利用対象外です";
      return;
    }
  };

  const areaClear = async (): Promise<void> => {
    deliveryRequestCreateInput.area_code = "";
    deliveryAreaName.value = "";
    deliveryAvailabilityData.value = [];
    disp_deliveryAvailabilityData.value = [];
    sectionBlockList.value = null;
    blockList.value = [];
    originalCityAddress.value = "";
    originalSectionAddress.value = "";
    deliveryRequestCreateInput.delivery_address1 =
      originalAddress.value + originalBlockName.value;
    deliveryRequestCreateInput.delivery_zip_code = "";
    deliveryId_0_Flg.value = false;
    deliveryStartHourItem.value = [];
    deliveryStartHourValues.value = undefined;
    deliveryStartMinitValues.value = undefined;
    displayDeliveryTime.value = "";
    deliverySelectDayBox.value = "-999";
    deliverySelectBox.value = "-999";
  };

  const areaAddresSelectClear = async (): Promise<void> => {
    pref_name.value = "";
    prefSelectValue.value = "";
    cityBlockList.value = null;
    sectionBlockList.value = null;
    blockList.value = [];
    addressBlockList.value = [];
    originalCityAddress.value = "";
    originalSectionAddress.value = "";
    originalBlockName.value = "";
  };

  const onClickToggleZip = async (): Promise<void> => {
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";
    btnNonMemberToggle.value = 0;
  };

  const onClickTogglePref = async (): Promise<void> => {
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";
    btnNonMemberToggle.value = 1;
  };

  const onPrefSelectInput = async (): Promise<void> => {
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";
  };

  // エリア選択ダイアログの戻る分岐を共通化し、画面固有の戻り先と初期化だけを指定する。
  const { onClickAreaDialogReturn } = useLegacyAreaDialogNavigation({
    blockList: blockList as Ref<Array<unknown> | null | undefined>,
    sectionBlockList: sectionBlockList as Ref<unknown | null | undefined>,
    originalCityAddress,
    originalSectionAddress,
    unassignedBlockList,
    shouldBackToAddressDialog: () =>
      btnNonMemberToggle.value == 0 &&
      (sectionBlockList.value == null ||
        blockList.value === null ||
        blockList.value === undefined ||
        blockList.value.length == 0),
    onBackToAddressDialog: async () => {
      await areaClear();
      areaDialog.value = false;
      addressDialog.value = true;
    },
    onDefaultExit: () => {
      // 都道府県検索から戻る場合は未入力状態へ戻す。
      if (btnNonMemberToggle.value === 1) {
        pref_name.value = "";
        prefSelectValue.value = "";
        originalAddress.value = "";
        originalBlockName.value = "";
        deliveryRequestCreateInput.delivery_address1 = "";
        deliveryRequestCreateInput.delivery_zip_code = "";
      } else {
        pref_name.value = pref_name_befor.value;
      }
      areaDialog.value = false;
    },
  });

  return {
    initializeAddress,
    onClickAddressUpdate,
    onClickAddressChangeBtn,
    onClickCityBlockLink,
    onClickSectionBlockLink,
    onClickAddressBlockLink,
    onClickListDeliveryArea,
    onClickAreaDialogReturn,
    onClickToggleZip,
    onClickTogglePref,
    onPrefSelectInput,
    onZipCodeInput,
    areaClear,
    areaAddresSelectClear,
    getAreaList,
    getAreaInfo,
    setDeliveryAreaSectionUnassigned,
  };
};
